import { Link } from '@tanstack/react-router';
import { ArrowSquareOutIcon, PencilSimpleIcon } from '@phosphor-icons/react';
import { T } from 'gt-react';
import { Action, Subject } from '@erp/authz';
import type { AgentPageResponse, AgentRunSummary } from '@erp/rpc';
import { Button } from 'src/components/ui/button';
import { useCan } from 'src/hooks/useCan';
import paths from 'src/paths';
import { PageIcon } from 'src/components/task-pages/taskPageIcons';
import { AgentPageRunControls } from './AgentPageRunControls';
import { RunStatusPill } from './RunStatusPill';

/**
 * Compact header of an agent page: identity (icon, name, description), the
 * agent it is mounted on, what the agent is doing right now, and Run / Stop.
 * The deep-dive monitor is one click away but not in the way.
 */
export function AgentPageHeader({
  page,
  latestRun,
  orchestrated,
}: {
  page: AgentPageResponse;
  latestRun: AgentRunSummary | null;
  /** Task-management agent: Stop can wait for the current task. */
  orchestrated: boolean;
}) {
  // Running while a run is in flight. A failed latest run stays Failed so a
  // paused resource does not look like the agent simply went idle. A
  // completed run is idle: the agent finished and is waiting for the next kick.
  const liveStatus =
    latestRun?.status === 'running' || latestRun?.status === 'failed'
      ? latestRun.status
      : null;
  // Same gate as the `/agents` route the definition lives under.
  const [canReadAgents] = useCan(Action.Read, Subject.BackgroundAgent);

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex min-w-0 items-start gap-3">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-border bg-stone-50">
          <PageIcon iconKey={page.icon} className="size-5 text-stone-600" />
        </div>
        <div className="min-w-0 space-y-0.5">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="truncate text-xl font-semibold">{page.name}</h1>
            <RunStatusPill status={liveStatus} />
            {!page.agentDefinitionIsActive && (
              <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                <T context="agent page badge when the mounted agent is inactive or archived">
                  Agent inactive
                </T>
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            {page.description && <span>{page.description}</span>}
            {/* Only worth saying when the page is named differently from its agent. */}
            {page.agentDefinitionName !== page.name && (
              <>
                {page.description && ' · '}
                <T context="agent page subtitle, which background agent the page is mounted on">
                  Agent:
                </T>{' '}
                <span className="text-foreground">
                  {page.agentDefinitionName}
                </span>
              </>
            )}
          </p>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <AgentPageRunControls
          page={page}
          latestRun={latestRun}
          orchestrated={orchestrated}
        />
        {canReadAgents && (
          <Button variant="ghost" size="sm" asChild>
            <Link
              to={paths.authenticated.backgroundAgents.definition}
              params={{ agentDefinitionId: page.agentDefinitionId }}
            >
              <PencilSimpleIcon className="mr-1 h-4 w-4" />
              <T context="link from an agent page to the agent definition where it can be edited">
                Edit definition
              </T>
            </Link>
          </Button>
        )}
        <Button variant="ghost" size="sm" asChild>
          <Link
            to="/agent-monitor/$agentDefinitionId"
            params={{ agentDefinitionId: page.agentDefinitionId }}
          >
            <ArrowSquareOutIcon className="mr-1 h-4 w-4" />
            <T context="link from an agent page to the full agent monitor">
              Monitor
            </T>
          </Link>
        </Button>
      </div>
    </div>
  );
}
